import { isNotFoundError } from "@/utils/error"
import { retry } from "@/utils/retry"
import { resolveCatalogFileName } from "./catalog-file-name"

// Most catalog file mutations still go through CatalogWorker so file access stays
// centralized around the worker's lifecycle. We also expose the same low-level
// operations here for Safari-sensitive flows that should avoid passing
// FileSystemDirectoryHandle through Comlink and can safely operate from the main thread.

async function findFileHandle(props: {
	directory: FileSystemDirectoryHandle
	filename: string
}): Promise<FileSystemHandle | undefined> {
	try {
		const handle = await props.directory.getFileHandle(props.filename, { create: false })
		return handle
	} catch (error) {
		if (error instanceof Error && error.name === "NotFoundError") {
			for await (const handle of props.directory.values()) {
				if (handle.kind === "directory") {
					const foundHandle = await findFileHandle({
						directory: handle,
						filename: props.filename,
					})
					if (foundHandle) {
						return foundHandle
					}
				}
			}
			return undefined
		}
		throw error
	}
}

export async function removeDirectoryEntry(props: {
	directory: FileSystemDirectoryHandle
	filename: string
	recursive?: boolean
	ignoreNotFound?: boolean
}): Promise<void> {
	if (props.recursive) {
		const handle = await findFileHandle({
			directory: props.directory,
			filename: props.filename,
		})
		if (!handle) {
			return
		}
		try {
			// @ts-expect-error non-standard api
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			await handle.remove()
		} catch (error) {
			if (!props.ignoreNotFound || !isNotFoundError(error)) {
				throw error
			}
		}
		return
	}

	try {
		await retry(async () => {
			await props.directory.removeEntry(props.filename, {
				recursive: false,
			})
		}, ["NotFoundError"])
	} catch (error) {
		if (!props.ignoreNotFound || !isNotFoundError(error)) {
			throw error
		}
	}
}

export async function removeProjectCatalogFile(
	directory: FileSystemDirectoryHandle,
	options: { ignoreNotFound?: boolean } = {},
): Promise<void> {
	const catalogFileName = await resolveCatalogFileName(directory)
	await removeDirectoryEntry({
		directory,
		filename: catalogFileName,
		ignoreNotFound: options.ignoreNotFound,
	})
}

export async function removeProjectStyleFile(
	directory: FileSystemDirectoryHandle,
	options: { ignoreNotFound?: boolean } = {},
): Promise<void> {
	await removeDirectoryEntry({
		directory,
		filename: "polarr.style",
		ignoreNotFound: options.ignoreNotFound,
	})
}

export async function removeProjectCatalogArtifacts(
	directory: FileSystemDirectoryHandle,
	options: { ignoreNotFound?: boolean } = {},
): Promise<void> {
	await removeProjectCatalogFile(directory, options)
	await removeProjectStyleFile(directory, options)
}
